import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Swiper, SwiperSlide } from "swiper/react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import SPYBannerAM from "../../../assets/no-loss/NoLossAM.png";
import SPYBannerRU from "../../../assets/no-loss/NoLossRU.png";
import SPYBannerEN from "../../../assets/no-loss/NoLossEN.png";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import { accountStore } from "entities/account";
import Button from "shared/ui/button";
import Modal from "widgets/auth/modal";
import { DealsTable } from "widgets/market";
import {
  formatPrice,
  getMLValue,
  getPreviewIccContractUrl,
  getCdnUrl,
  navigateToAsset,
  canCancelDeal,
} from "helpers/functions";
import { AssetType, OperationType, OperationStatus } from "static/types";
import useCanAction from "hooks/useCanAction";
import { ActionBtn } from "../components/action-btn";
import HorizontalScroll from "../components/horizontal-scroll";
import { TabBtn } from "../components/tab-btn";
import ReviewDetails from "../components/review-details-row";
import ReportItem from "../components/report-item";
import Loading from "widgets/loading";
import TgIcon from "../../../assets/icons8-telegram-94.png";
import moment from "moment-timezone";
import { ChartViewCustom } from "widgets/market/chart-view";
import { useLocalizedImage } from "hooks/useLocalizedImage";
import Info from "../../../assets/strategy/SpyInfo.svg";
import { PATH_LIST } from "shared/lib/react-router";
import Checkmark from "../../../assets/strategy/uate/Checkmark.svg";
import Photo1 from "../../../assets/strategy/uate/Photo1.png";
import Photo2 from "../../../assets/strategy/uate/Photo2.png";
import Logo1 from "../../../assets/strategy/uate/Logo1.svg";
import Logo2 from "../../../assets/strategy/uate/Logo2.svg";
import Logo3 from "../../../assets/strategy/uate/Logo3.svg";
import Logo4 from "../../../assets/strategy/uate/Logo4.svg";
import Logo5 from "../../../assets/strategy/uate/Logo5.svg";
import Logo6 from "../../../assets/strategy/uate/Logo6.svg";
import Logo7 from "../../../assets/strategy/uate/Logo7.svg";
import Logo8 from "../../../assets/strategy/uate/Logo8.svg";
import Logo9 from "../../../assets/strategy/uate/Logo9.svg";
import Tech1 from "../../../assets/strategy/uate/Tech1.png";
import Tech2 from "../../../assets/strategy/uate/Tech2.png";
import Mockup from "../../../assets/strategy/uate/Mockup.png";
import UATEBannerRU from "../../../assets/strategy/uate/UATEBannerRU.png";
import UATEBannerEN from "../../../assets/strategy/uate/UATEBannerEN.png";
import UATEBannerAM from "../../../assets/strategy/uate/UATEBannerAM.png";

const RISK_TYPES = ["Low Risk", "Average Risk", "High Risk"] as const;

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  isExpired: boolean;
}

export const StrategyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { asset_id } = useParams();
  const { t, i18n } = useTranslation("app");
  const { t: err } = useTranslation("errors");
  const { t: i } = useTranslation("investingWithoutLoss");
  const { t: u } = useTranslation("union");
  const action = useCanAction();
  const {
    isLoading,
    assetInfo,
    loadAssetInfo,
    loadAssetDeals,
    assetDeals,
    cancelDeal,
  } = useStore(marketStore);
  const { getDocuments, signIccContract } = useStore(accountStore);

  const strategy = assetInfo;
  const symbol = strategy?.asset?.symbol || "";
  const risk = strategy?.asset?.data?.risk;
  const annualYield = strategy?.asset?.data?.annual_yield;
  // const currency = strategy?.asset?.currency?.code;
  const currencySymbol = strategy?.asset?.currency?.symbol;

  const account = strategy?.account;
  const accountExists = account && account.quantity > 0;

  const customInfo = strategy?.customInfo || null;

  const contractType = customInfo?.info?.contract_type || null;
  // const contractAmountType = customInfo?.info?.contract_amount_type || null;
  const canTradeFromDate = customInfo?.info?.can_trade_from_date || null;
  const enableTrading =
    (strategy && "enableTrading" in strategy ? strategy.enableTrading : true) &&
    (canTradeFromDate
      ? moment(canTradeFromDate, "YYYY-MM-DD").isSameOrBefore(moment())
      : true);
  const isTradingClosed = !!customInfo?.info?.disable_trading;
  const investButtonText = isTradingClosed
    ? t("applicationsClosed")
    : t("invest");
  const isInvestButtonDisabled = !enableTrading || isTradingClosed;
  const capitalProtected = strategy?.capital_protected || "";

  const tabs = customInfo?.info?.tabs || [];
  const defaultTab = tabs.length > 0 ? "tab-1" : "review";

  const allocationResults = customInfo?.info?.allocation_results || null;
  const isSPY = assetInfo?.asset?.symbol === "SPDR";
  const isUATE = assetInfo?.asset?.symbol === "UATE_CN";

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [docs, setDocs] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [cancelOrderModal, setCancelOrderModal] = useState(false);
  const [disable, setDisabled] = useState(false);
  const [reportId, setReportId] = useState<number>();
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    isExpired: false,
  });

  const prevAccountExists = useRef<boolean | null | undefined>(undefined);

  const activeTabContent = tabs.find((_, i) => `tab-${i + 1}` === activeTab);

  const pendingDeal =
    assetDeals.length > 0 &&
    (assetDeals[0].status === OperationStatus.Actively ||
      assetDeals[0].status === OperationStatus.OnPerformance);

  const calculateTimeRemaining = useCallback(
    (protectionEndDate: string): TimeRemaining => {
      const now = moment();
      const endDate = moment.utc(protectionEndDate, "YYYY-MM-DD HH:mm");
      const remaining = endDate.diff(now);

      if (remaining <= 0) {
        return { days: 0, hours: 0, minutes: 0, isExpired: true };
      }

      const duration = moment.duration(remaining);
      return {
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        minutes: duration.minutes(),
        isExpired: false,
      };
    },
    [],
  );

  const handleNavigate = () => {
    if (isSPY) {
      navigate(PATH_LIST.home);
    } else {
      navigate(-1);
    }
  };

  const handleTabClick = (text: string) => {
    setActiveTab(text);
    scroll();
  };

  const scroll = () => {
    // Scroll window first (most reliable for mobile apps)
    window.scrollTo(0, 0);

    // Try to find and scroll the scrollable containers
    const strategyMain = document.querySelector(".strategy-main");
    const strategyPage = document.querySelector(
      ".strategy-page, .strategy-page-no-loss",
    );
    const stockDetails = document.querySelector(".stock-details");

    if (strategyMain) {
      strategyMain.scrollTop = 0;
    }

    if (strategyPage) {
      strategyPage.scrollTop = 0;
    }

    if (stockDetails) {
      stockDetails.scrollTop = 0;
    }
  };

  const onSignContract = async (pinCode: string) => {
    setDisabled(true);
    try {
      await signIccContract(pinCode, reportId!, err, () => {
        window.location.reload();
      });
    } catch (error) {
      toast.error("Server Error");
    } finally {
      setModal(false);
      setDisabled(false);
    }
  };

  useEffect(() => {
    loadAssetInfo(asset_id!).then(() => {
      loadAssetDeals(+asset_id!);
    });
  }, []);

  useEffect(() => {
    if (symbol) {
      getDocuments(symbol).then((data: any) => {
        setDocs(data.data);
      });
    } else {
      setDocs([]);
    }
  }, [symbol]);

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state?.tab || "review");
    } else if (accountExists || assetDeals.length > 0) {
      setActiveTab("deals");
    } else {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, accountExists, assetDeals.length, location]);

  // Update countdown timer
  useEffect(() => {
    if (account?.data && isSPY) {
      // Initial calculation
      setTimeRemaining(calculateTimeRemaining(account.data.protection_date));

      // Update every minute
      const interval = setInterval(() => {
        setTimeRemaining(calculateTimeRemaining(account.data.protection_date));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [account?.data, isSPY, calculateTimeRemaining]);

  const getTotalAmount = () => {
    try {
      return formatPrice(+(account!.quantity * account!.price).toFixed(2));
    } catch (error) {
      return "";
    }
  };

  const assetPrice = useMemo(() => {
    const bid = assetInfo?.asset?.bid;
    const ask = assetInfo?.asset?.ask;
    const nominal = assetInfo?.asset?.data?.nominal || 100;
    if (bid && ask) {
      const quote =
        parseFloat((((bid + ask) / 2 / 100) * nominal).toFixed(2)) || 0;
      return quote || account?.price || 0;
    }
    return account?.price || 0;
  }, [assetInfo, account]);

  const pnl = useMemo(() => {
    try {
      const summaryPnl = +(
        account!.quantity * assetPrice -
        account!.quantity * account!.price
      ).toFixed(4);

      return summaryPnl;
    } catch (error) {
      return "";
    }
  }, [account, assetPrice]);

  const assetProgress = useMemo(() => {
    if (!account) return 0;
    const { price } = account;
    const i = price / 100;
    const diff = +(assetPrice - price).toFixed(2);
    return +(diff / i).toFixed(2);
  }, [assetPrice, account]);

  const SPYBanner = useLocalizedImage({
    am: SPYBannerAM,
    ru: SPYBannerRU,
    en: SPYBannerEN,
  });

  const UATEBanner = useLocalizedImage({
    am: UATEBannerAM,
    ru: UATEBannerRU,
    en: UATEBannerEN,
  });

  const spyTab2Items = [
    i("term1"),
    i("term2"),
    i("term3"),
    i("term4"),
    i("term5"),
    i("term6"),
    i("term7"),
    i("term7_1"),
    i("term7_2"),
    i("term8"),
    i("term9"),
    i("term10"),
    i("term11"),
    i("term12"),
  ];

  const spyTab3Items = [i("ex1"), i("ex2"), i("ex3")];

  const spyTab4Items = [
    {
      question: i("faq1"),
      answer: i("faq1_2"),
    },
    {
      question: i("faq2"),
      answer: i("faq2_2"),
    },
    {
      question: i("faq3"),
      answer: i("faq3_2"),
    },
    {
      question: i("faq4"),
      answer: i("faq4_2"),
    },
    {
      question: i("faq5"),
      answer: i("faq5_2"),
    },
    {
      question: i("faq6"),
      answer: i("faq6_2"),
    },
    {
      question: i("faq7"),
      answer: i("faq7_2"),
    },
    {
      question: i("faq8"),
      answer: i("faq8_2"),
    },
    {
      question: i("faq9"),
      answer: i("faq9_2"),
    },
  ];

  const onCancelOrder = () => {
    if (assetDeals.length > 0 && pendingDeal) {
      const deal = assetDeals[0];
      const { canCancel, errorMessage } = canCancelDeal(deal, t);

      if (canCancel) {
        cancelDeal(deal.id);
        toast.success(i("orderCanceled"));
        setCancelOrderModal(false);
        setTimeout(() => {
          loadAssetDeals(+asset_id!);
        }, 500);
      } else {
        toast.error(errorMessage);
        setCancelOrderModal(false);
      }
    } else {
      toast.error(t("cannotCancelOrder"));
      setCancelOrderModal(false);
    }
  };

  // useEffect(() => {
  //   console.log(accountExists);
  //   console.log("====================================");
  //   console.log(assetDeals.length);
  //   console.log("====================================");
  // }, [accountExists, assetDeals.length]);

  useEffect(() => {
    if (
      prevAccountExists.current !== undefined &&
      prevAccountExists.current !== accountExists
    ) {
      window.location.reload();
    }
    prevAccountExists.current = accountExists;
  }, [accountExists]);

  const Logos = [Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7, Logo8, Logo9];

  const faq = [
    {
      question: u("faq1Question"),
      answer: u("faq1Answer"),
    },
    {
      question: u("faq2Question"),
      answer: u("faq2Answer"),
    },
    {
      question: u("faq3Question"),
      answer: u("faq3Answer"),
    },
    {
      question: u("faq4Question"),
      answer: u("faq4Answer"),
    },
    {
      question: u("faq5Question"),
      answer: u("faq5Answer"),
      title: u("faq5ListTitle"),
      list: [u("faq5ListItem1"), u("faq5ListItem2"), u("faq5ListItem3")],
    },
    {
      question: u("faq6Question"),
      answer: u("faq6Answer"),
    },
    {
      question: u("faq7Question"),
      answer: u("faq7Answer"),
    },
    {
      question: u("faq8Question"),
      answer: u("faq8Answer"),
    },
    {
      question: u("faq9Question"),
      answer: u("faq9Answer"),
    },
    {
      question: u("faq10Question"),
      answer: u("faq10Answer"),
    },
  ];

  if (isLoading || !assetInfo || ((isSPY || isUATE) && !customInfo)) {
    return <Loading />;
  }

  if (isSPY && customInfo) {
    return (
      <div className="strategy-page-no-loss">
        <div className="strategy-header-container">
          <div className="strategy-header">
            <img
              className="go-back-strategy"
              onClick={handleNavigate}
              src={GoBack}
              alt="Go Back"
            />
          </div>

          <img className="spy-banner" src={SPYBanner} alt="SPY Banner" />
        </div>

        <HorizontalScroll className="tab-bar tab-bar-cat">
          {[
            {
              key: "deals",
              title: t("deals"),
              condition: accountExists || assetDeals.length > 0,
            },
            ...customInfo.info.tabs.map((tab, i) => ({
              key: `tab-${i + 1}`,
              title: getMLValue(tab.title, i18n.language) || "tab",
              condition: true,
            })),
          ]
            .filter(({ condition }) => condition)
            .map(({ key, title }) => (
              <TabBtn
                key={key}
                text={title}
                active={activeTab === key}
                onClick={() => handleTabClick(key)}
              />
            ))}
        </HorizontalScroll>

        <div
          className="strategy-main"
          style={{ paddingTop: customInfo ? "0px" : "24px" }}
        >
          {activeTab === "deals" ? (
            <div className="sm-tab-name sm-tab-trading">
              <div className="current-investment-amount-strategy">
                <label>{t("currentInvestmentAmount")}</label>
                <p>
                  {accountExists
                    ? getTotalAmount()
                    : pendingDeal
                    ? formatPrice(
                        +(assetDeals[0].quantity * assetDeals[0].price).toFixed(
                          2,
                        ),
                      )
                    : 0}
                  {currencySymbol}
                </p>

                {!pendingDeal && !!pnl && (
                  <span className={pnl > 0 ? "success" : "danger"}>
                    {pnl > 0 ? "+" : ""}
                    {pnl}
                    {currencySymbol}{" "}
                    {!!assetProgress && (
                      <>
                        ({assetProgress > 0 ? "+" : ""}
                        {assetProgress}%)
                      </>
                    )}
                  </span>
                )}
              </div>

              {pendingDeal && (
                <div className="spy-strategy-pending-deal-info-block">
                  <img src={Info} alt="Info" />
                  <p>{i("requestAccepted")}</p>
                </div>
              )}

              {pendingDeal && (
                <button
                  className="spy-strategy-pending-deal-cancel-btn"
                  onClick={() => setCancelOrderModal(true)}
                >
                  {i("cancelOrder")}
                </button>
              )}

              {account && !pendingDeal && (
                <div className="strategy-dates-info">
                  <div className="date-info-item">
                    <span className="date-label">{i("purchasedOn")}</span>
                    <span className="date-value">
                      {moment(account.date).format("DD.MM.YYYY")}
                    </span>
                  </div>
                  <hr />
                  {account?.data?.protection_date && (
                    <>
                      <div className="date-info-item">
                        <span className="date-label">
                          {i("protectedUntil")}
                        </span>
                        <span className="date-value">
                          {moment(account.data.protection_date).format(
                            "DD.MM.YYYY",
                          )}
                        </span>
                      </div>

                      <div className="countdown-timer">
                        {timeRemaining.isExpired ? (
                          // todo change this part
                          <span className="countdown-expired">
                            {i("protectionExpired")}
                          </span>
                        ) : (
                          <>
                            <span className="countdown-label">
                              {i("timeLeftUntilProtectionEnds")}
                            </span>
                            <div className="countdown-values">
                              <div className="countdown-item">
                                <span className="countdown-number">
                                  {timeRemaining.days}
                                </span>
                                <span className="countdown-unit">
                                  {i("days")}
                                </span>
                              </div>
                              <span className="countdown-separator">:</span>
                              <div className="countdown-item">
                                <span className="countdown-number">
                                  {String(timeRemaining.hours).padStart(2, "0")}
                                </span>
                                <span className="countdown-unit">
                                  {i("hours")}
                                </span>
                              </div>
                              <span className="countdown-separator">:</span>
                              <div className="countdown-item">
                                <span className="countdown-number">
                                  {String(timeRemaining.minutes).padStart(
                                    2,
                                    "0",
                                  )}
                                </span>
                                <span className="countdown-unit">
                                  {i("minutes")}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {!pendingDeal && (
                <div className="strategy-important-info-block">
                  {[i("lossProtectionCompensation"), i("holdOrSell")].map(
                    (item, index) => (
                      <p
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          ) : activeTabContent && activeTab === "tab-1" ? (
            <div className="sm-tab-name">
              <div className="spy-content-header">
                <h4>{i("firstSecureInvestingExperience")}</h4>
                <p>{i("invest")}</p>
              </div>

              <div className="spy-content-how-it-works">
                <h4>{i("howItWorks")}</h4>

                <div className="spy-content-how-it-works-items-wrapper">
                  {[
                    i("startInvesting"),
                    i("minMaxInvestment"),
                    // i("approvalWithinOneBusinessDay"),
                    i("lossProtection"),
                    i("holdInvestment"),
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="spy-content-how-it-works-item-wrapper"
                    >
                      <div className="spy-content-how-it-works-item">
                        <p dangerouslySetInnerHTML={{ __html: item }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="spy-content-protection-result">
                <h4>{i("atTheEndOfThe30Days")}</h4>

                <p
                  dangerouslySetInnerHTML={{
                    __html: "📉 " + i("ifYourInvestmentIsDown"),
                  }}
                />
                <p
                  dangerouslySetInnerHTML={{
                    __html: "📈 " + i("ifItIsUp"),
                  }}
                />
                <p>{i("ifInvestmentAmountIsBetween50And1000")}</p>
              </div>

              <div className="spy-content-important-info-block">
                {[i("commission"), i("loss"), i("promotionValid")].map(
                  (item, index) => (
                    <p key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ),
                )}
              </div>
            </div>
          ) : activeTabContent && activeTab === "tab-2" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4
                dangerouslySetInnerHTML={{ __html: i("termsOfThePromotion") }}
              />
              <ul>
                {spyTab2Items.map((item, index) => (
                  <li key={index}>
                    <div>{index + 1}</div>
                    <p dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
          ) : activeTabContent && activeTab === "tab-3" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4
                dangerouslySetInnerHTML={{ __html: i("exampleTransaction") }}
              />
              <ul>
                {spyTab3Items.map((item, index) => (
                  <li className={`spy-tab-3-item-li-${index + 1}`} key={index}>
                    <div>{index + 1}</div>
                    <p dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>

              <p
                className="spy-tab-3-description"
                dangerouslySetInnerHTML={{ __html: i("SPYInfoText") }}
              />
            </div>
          ) : activeTabContent && activeTab === "tab-4" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4>{i("faq")}</h4>
              <ul>
                {spyTab4Items.map((item, index) => (
                  <li key={index}>
                    <div>{index + 1}</div>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: `<h5>${item.question}</h5> ${item.answer}`,
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <></>
          )}
        </div>

        {!accountExists && !pendingDeal && (
          <div className="btn-wrapper deal-btn-wrapper">
            <Button
              type="submit"
              variant="dark"
              className="customBtn deal-btn"
              disabled={isInvestButtonDisabled}
              onClick={() =>
                action(() =>
                  navigateToAsset(
                    AssetType.StructuredProduct,
                    asset_id,
                    navigate,
                    OperationType.Buy,
                  ),
                )
              }
            >
              {i("startInvestingBtn")}
            </Button>
          </div>
        )}

        {modal && (
          <Modal
            customModal="modalLogOut"
            onClick={onSignContract}
            onHide={() => {
              setModal(false);
              setDisabled(false);
            }}
            title={t("signContract")}
            text={t("enterPin")}
            btnText={t("sign")}
            showSecondBtn={false}
            customOutlet="customOutlet"
            confirmDeal={true}
            isLoading={isLoading || disable}
            dealError={false}
          />
        )}

        {cancelOrderModal && (
          <Modal
            customModal="modalLogOut spy-cancel-order-modal"
            onClick={onCancelOrder}
            onHide={() => {
              setCancelOrderModal(false);
              setDisabled(false);
            }}
            title={i("cancelOrder")}
            text={i("sureToCancel")}
            btnText={i("yes")}
            btnText2={i("no")}
            onClickSecondBtn={() => {
              setCancelOrderModal(false);
            }}
            showSecondBtn={true}
            customOutlet="customOutlet"
          />
        )}
      </div>
    );
  }

  if (isUATE && customInfo) {
    return (
      <div className="strategy-page-no-loss strategy-page-uate">
        <div className="strategy-header-container">
          <div className="strategy-header">
            <img
              className="go-back-strategy"
              onClick={handleNavigate}
              src={GoBack}
              alt="Go Back"
            />
          </div>

          <img className="spy-banner" src={UATEBanner} alt="SPY Banner" />
        </div>

        <HorizontalScroll className="tab-bar tab-bar-cat">
          {[
            {
              key: "deals",
              title: t("deals"),
              condition: accountExists || assetDeals.length > 0,
            },
            ...customInfo.info.tabs.map((tab, i) => ({
              key: `tab-${i + 1}`,
              title: getMLValue(tab.title, i18n.language) || "tab",
              condition: true,
            })),
          ]
            .filter(({ condition }) => condition)
            .map(({ key, title }) => (
              <TabBtn
                key={key}
                text={title}
                active={activeTab === key}
                onClick={() => handleTabClick(key)}
              />
            ))}
        </HorizontalScroll>

        <div
          className="strategy-main"
          style={{ paddingTop: customInfo ? "0px" : "24px" }}
        >
          {activeTab === "deals" ? (
            <div className="sm-tab-name sm-tab-trading">
              <div className="current-investment-amount-strategy">
                <label>{t("currentInvestmentAmount")}</label>
                <p>
                  {accountExists
                    ? getTotalAmount()
                    : pendingDeal
                    ? formatPrice(
                        +(assetDeals[0].quantity * assetDeals[0].price).toFixed(
                          2,
                        ),
                      )
                    : 0}
                  {currencySymbol}
                </p>

                {!pendingDeal && !!pnl && (
                  <span className={pnl > 0 ? "success" : "danger"}>
                    {pnl > 0 ? "+" : ""}
                    {pnl}
                    {currencySymbol}{" "}
                    {!!assetProgress && (
                      <>
                        ({assetProgress > 0 ? "+" : ""}
                        {assetProgress}%)
                      </>
                    )}
                  </span>
                )}
              </div>

              {pendingDeal && (
                <div className="spy-strategy-pending-deal-info-block">
                  <img src={Info} alt="Info" />
                  <p>{u("applicationUnderReview")}</p>
                </div>
              )}

              {pendingDeal && (
                <ul className="uate-strategy-pending-deal-info-list">
                  <li>{u("weWillSendYouNotification")}</li>
                  <li>{u("afterApproval")}</li>
                  <li>{u("signedContract")}</li>
                </ul>
              )}

              {account && !pendingDeal && (
                <div className="strategy-dates-info">
                  {docs.length > 0 ? (
                    <div
                      className="date-info-item"
                      style={{ flexDirection: "column", gap: "8px" }}
                    >
                      {docs.map((report, index) => (
                        <>
                          <ReportItem
                            key={index}
                            url={report.file}
                            name={`${t("agreementFor")} (${report.date})`}
                          />
                          {index < docs.length - 1 && <hr />}
                        </>
                      ))}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              )}
            </div>
          ) : activeTabContent && activeTab === "tab-1" ? (
            <div className="sm-tab-name">
              <div className="spy-content-header">
                <p
                  dangerouslySetInnerHTML={{
                    __html: u("heroSubtitle") + " " + u("heroDescription"),
                  }}
                />
              </div>

              <div className="spy-content-how-it-works">
                <h4>{u("whatIsTitle")}</h4>

                <div className="spy-content-how-it-works-items-wrapper">
                  {[
                    u("whatIsDescription"),
                    u("minimumInvestment"),
                    u("bondIssuance"),
                    u("automaticConversion"),
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="spy-content-how-it-works-item-wrapper"
                    >
                      <div className="spy-content-how-it-works-item">
                        <p dangerouslySetInnerHTML={{ __html: item }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="spy-content-allows-you">
                <h4>{u("benefitsTitle")}</h4>

                {[u("benefit1"), u("benefit2"), u("benefit3")].map(
                  (item, index) => (
                    <div key={index}>
                      <img src={Checkmark} alt="Checkmark" />
                      <p>{item}</p>
                    </div>
                  ),
                )}

                <span>{u("benefitsDescription")}</span>
              </div>
            </div>
          ) : activeTabContent && activeTab === "tab-2" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4>{u("purchaseRequirements")}</h4>

              <div className="uate-tab-2-steps">
                {[
                  u("purchaseRequirements2"),
                  u("purchaseRequirements3"),
                  u("purchaseRequirements4"),
                  u("purchaseRequirements5"),
                  u("purchaseRequirements6"),
                  u("purchaseRequirements7"),
                ].map((item, index) => (
                  <div key={index}>
                    <h4>
                      {u("stepLabel")} {index + 1}
                    </h4>
                    <span>{item}</span>
                  </div>
                ))}

                <div>
                  <p>{u("step4Title")}</p>
                  <span>{u("step4Description")}</span>
                </div>
              </div>
            </div>
          ) : activeTabContent && activeTab === "tab-3" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4>{u("uateDescription1")}</h4>

              <div className="uate-tab-3-photos">
                <img src={Photo1} alt="Photo1" />
                <img src={Photo2} alt="Photo2" />
              </div>

              <p
                className="uate-tab-3-text"
                dangerouslySetInnerHTML={{
                  __html:
                    u("uateDescription2") +
                    "<br/><br/>" +
                    u("uateDescription3") +
                    "<b> NVIDIA, Adobe, Team Telecom Armenia, Yandex, Krisp, EasyPay, AMD, Microchip, DataArt, ServiceTitan, SoftConstruct, JetBrains, EPAM </b>" +
                    u("andOthers"),
                }}
              />

              {/* <div className="uate-tab-3-logos">
                {Logos.map((item, index) => (
                  <div key={index}>
                    <img src={item} alt={item} />
                  </div>
                ))}
                <div>
                  <p>{u("andOthers")}</p>
                </div>
              </div> */}

              <h4>{u("uateBeyondTitle")}</h4>

              <div className="uate-tab-3-tech">
                <img src={Tech1} alt="Tech1" />
                <h4>{u("uateDigitecTitle")}</h4>
                <p>{u("uateDigitecDescription")}</p>
              </div>

              <div className="uate-tab-3-tech">
                <img src={Tech2} alt="Tech2" />
                <h4>{u("uateArmatTitle")}</h4>
                <p>{u("uateArmatDescription")}</p>
              </div>

              <p className="uate-tab-3-text">{u("uateFounded")}</p>

              <div className="uate-tab-3-about-sirius">
                <div>
                  <h4
                    dangerouslySetInnerHTML={{
                      __html: u("aboutSiriusTitle"),
                    }}
                  />
                  <p
                    dangerouslySetInnerHTML={{
                      __html: u("siriusDescription"),
                    }}
                  />
                </div>

                <img src={Mockup} alt="Mockup" />
              </div>
            </div>
          ) : activeTabContent && activeTab === "tab-4" ? (
            <div className="sm-tab-name spy-tab-2">
              <h4>{u("faqTitle")}</h4>

              <ul className="uate-tab-4-faq">
                {faq.map((item, index) => (
                  <li key={index}>
                    <h5>
                      {index + 1}. {item.question}
                    </h5>
                    <p dangerouslySetInnerHTML={{ __html: item.answer }} />

                    {item.title && <p>{item.title}</p>}

                    {item.list &&
                      item.list?.map((listItem, listIndex) => (
                        <p key={listIndex}> • {listItem}</p>
                      ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <></>
          )}
        </div>

        {!pendingDeal && (
          <div className="btn-wrapper deal-btn-wrapper">
            <Button
              type="submit"
              variant="dark"
              className="customBtn deal-btn"
              disabled={!enableTrading}
              onClick={() =>
                action(() =>
                  navigateToAsset(
                    AssetType.StructuredProduct,
                    asset_id,
                    navigate,
                    OperationType.Buy,
                  ),
                )
              }
            >
              {isTradingClosed
                ? t("applicationsClosed")
                : account
                ? t("invest")
                : u("heroButton")}
            </Button>
          </div>
        )}

        {modal && (
          <Modal
            customModal="modalLogOut"
            onClick={onSignContract}
            onHide={() => {
              setModal(false);
              setDisabled(false);
            }}
            title={t("signContract")}
            text={t("enterPin")}
            btnText={t("sign")}
            showSecondBtn={false}
            customOutlet="customOutlet"
            confirmDeal={true}
            isLoading={isLoading || disable}
            dealError={false}
          />
        )}

        {cancelOrderModal && (
          <Modal
            customModal="modalLogOut spy-cancel-order-modal"
            onClick={onCancelOrder}
            onHide={() => {
              setCancelOrderModal(false);
              setDisabled(false);
            }}
            title={i("cancelOrder")}
            text={i("sureToCancel")}
            btnText={i("yes")}
            btnText2={i("no")}
            onClickSecondBtn={() => {
              setCancelOrderModal(false);
            }}
            showSecondBtn={true}
            customOutlet="customOutlet"
          />
        )}
      </div>
    );
  }

  return (
    <div className="strategy-page">
      {customInfo && customInfo?.info?.header_image ? (
        <div
          className={`strategy-header-container strategy-header-container-${risk}`}
          style={{
            backgroundImage: `url(${getCdnUrl(
              `${symbol}/${getMLValue(
                customInfo?.info?.header_image,
                i18n.language,
              )}`,
              "strategy",
            )})`,
            // SPY_PROMO_TEMPLATE
            height: customInfo.symbol === "SPDR" ? "225px" : "180px",
            minHeight: customInfo.symbol === "SPDR" ? "225px" : "180px",
            backgroundSize: "100%",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="strategy-header">
            <img
              className="go-back-strategy"
              onClick={handleNavigate}
              src={GoBack}
              alt="Go Back"
            />
          </div>
        </div>
      ) : (
        <div
          className={`strategy-header-container strategy-header-container-${risk}`}
          style={{
            background: customInfo
              ? `linear-gradient(256.2deg, ${customInfo.info.background.to} -7.74%, ${customInfo.info.background.from} 100%)`
              : "",
            ...(customInfo?.info?.hide_header_cards && {
              minHeight: "72px",
              height: "72px",
            }),
          }}
        >
          <div className="strategy-header">
            <img
              className="go-back-strategy"
              onClick={handleNavigate}
              src={GoBack}
              alt="Go Back"
            />
            <p>{strategy?.asset?.name}</p>
          </div>
          {customInfo && customInfo?.info?.hide_header_cards ? (
            <></>
          ) : (
            <div className="strategy-header-info">
              {customInfo?.info?.header_cards ? (
                customInfo?.info?.header_cards?.map((hc) => (
                  <div className="sh-info-box">
                    {getMLValue(hc.p, i18n.language) && (
                      <p>{getMLValue(hc.p, i18n.language)}</p>
                    )}
                    {getMLValue(hc.span, i18n.language) && (
                      <span>{getMLValue(hc.span, i18n.language)}</span>
                    )}
                  </div>
                ))
              ) : (
                <>
                  <div className="sh-info-box">
                    <p>≈ {annualYield}%</p>
                    <span>{t("annualReturn")}</span>
                  </div>
                  {/* <div className="sh-info-box">
                    <p>
                      {strategy?.asset?.data?.minimal_invest} {currencySymbol}
                    </p>
                    <span>{t("initialInvestmentAmount")}</span>
                  </div> */}
                  <div className="sh-info-box">
                    <span>{t(RISK_TYPES[risk ? risk - 1 : 0])}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {customInfo && (
        <HorizontalScroll className="tab-bar tab-bar-cat">
          {[
            {
              key: "deals",
              title: t("deals"),
              condition: accountExists || assetDeals.length > 0,
            },
            ...customInfo.info.tabs.map((tab, i) => ({
              key: `tab-${i + 1}`,
              title: getMLValue(tab.title, i18n.language) || "tab",
              condition: true,
            })),
          ]
            .filter(({ condition }) => condition)
            .map(({ key, title }) => (
              <TabBtn
                key={key}
                text={title}
                active={activeTab === key}
                onClick={() => handleTabClick(key)}
              />
            ))}
        </HorizontalScroll>
      )}

      <div
        className="strategy-main"
        style={{ paddingTop: customInfo ? "0px" : "24px" }}
      >
        {!customInfo ? (
          <div className="sm-info-container">
            {/* <StrategyChart
                chart={strategy?.chart}
                title={chartSymbol ? `${chartSymbol} stock chart` : undefined}
              /> */}
            <DealsTable assetId={strategy?.asset?.id || undefined} />
          </div>
        ) : activeTab === "deals" ? (
          <div className="sm-tab-name sm-tab-trading">
            <div>
              <div
                className={
                  capitalProtected
                    ? "current-investment-amount-strategy-protected"
                    : "current-investment-amount-strategy"
                }
              >
                <div className="current-investment-amount-wrapper">
                  <label>{t("currentInvestmentAmount")}</label>
                  <p>
                    {accountExists ? getTotalAmount() : 0} {currencySymbol}
                  </p>
                  {!!pnl && isSPY && (
                    <>
                      <label htmlFor="">{t("P&L")}</label>
                      <span className={pnl > 0 ? "success" : "danger"}>
                        {pnl > 0 ? "+" : ""}
                        {pnl}
                        {currencySymbol}{" "}
                        {!!assetProgress && (
                          <>
                            ({assetProgress > 0 ? "+" : ""}
                            {assetProgress}%)
                          </>
                        )}
                      </span>
                    </>
                  )}
                </div>
                {capitalProtected && (
                  <div className="protection-amount-wrapper">
                    <label style={{ marginTop: "12px" }}>
                      Подключена{" "}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("capitalProtection");
                        }}
                      >
                        защита капитала.
                      </span>
                      <br />
                      Уплаченная премия:{" "}
                      <b>
                        {formatPrice(+capitalProtected.toFixed(2))}{" "}
                        {currencySymbol}
                      </b>
                    </label>
                  </div>
                )}
              </div>

              {docs.length ? (
                <div>
                  <p className="brokerage-section-name">{t("agreements")}</p>
                  <div className="report-date-item-wrapper">
                    {docs.map((report, index) =>
                      report.signed ? (
                        <ReportItem
                          key={index}
                          url={report.file}
                          name={`${t("agreementFor")} ${report.date}`}
                        />
                      ) : (
                        <ReportItem
                          key={index}
                          onClick={() => {
                            window.open(
                              getPreviewIccContractUrl(
                                report.user_id,
                                report.user_type,
                                report?.amount || 0,
                                contractType!,
                              ),
                              "_blank",
                            );
                          }}
                          name={`${t("agreementFor")} ${report.date}`}
                          needSign
                          onSign={() => {
                            setReportId(report.id);
                            setModal(true);
                          }}
                        />
                      ),
                    )}
                  </div>
                </div>
              ) : (
                <></>
                // <div className="not-found-coming-soon">
                //   <p>{t("notFound")}</p>
                // </div>
              )}

              {customInfo?.info?.telegram && accountExists ? (
                <div style={{ marginTop: "12px" }}>
                  <p className="brokerage-section-name">
                    {t("iccTelegramBanner")}
                  </p>
                  <div className="report-date-item-wrapper">
                    <ReportItem
                      onClick={() => {
                        window.open(customInfo.info.telegram, "_blank");
                      }}
                      name={`${t("Telegram channel")}`}
                      customIcon={TgIcon}
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}

              {allocationResults && (
                <div style={{ marginTop: "12px" }}>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: getMLValue(
                        allocationResults.title,
                        i18n.language,
                      ),
                    }}
                  />
                  <p style={{ fontSize: "15px", marginTop: "12px" }}>
                    {getMLValue(allocationResults.span, i18n.language)}
                  </p>

                  <table style={{ width: "100%", marginTop: "8px" }}>
                    <thead style={{ fontSize: "14px" }}>
                      <tr>
                        <th>
                          {getMLValue(
                            allocationResults.company_column_text,
                            i18n.language,
                          )}
                        </th>
                        <th>
                          {getMLValue(
                            allocationResults.shares_column_text,
                            i18n.language,
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocationResults.users[0]?.shares.map(
                        (shares, index) => (
                          <tr key={index} style={{ height: "32px" }}>
                            <td>{allocationResults.companies[index]}</td>
                            <td>{shares}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <DealsTable
              assetId={strategy?.asset?.id || undefined}
              noClearTrades={true}
            />
          </div>
        ) : activeTabContent ? (
          <div className="sm-tab-name">
            {activeTabContent?.data?.map((item, index) => {
              if (item.type === "companies_list") {
                return (
                  <Swiper
                    style={{ width: "100%", padding: "16px 24px" }}
                    wrapperClass="strategy-indicator-box"
                    spaceBetween={12}
                    slidesPerView={1.1}
                    scrollbar={{ draggable: true }}
                  >
                    {item.value.map((param, index) => (
                      <SwiperSlide key={index}>
                        <div className="indicator-box">
                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <img
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "100%",
                              }}
                              src={getCdnUrl(
                                `${strategy?.asset?.symbol}/${param.logo}`,
                                "strategy",
                              )}
                              alt={param.name}
                            />
                            <p>{param.name}</p>
                          </div>
                          {param.data.map((param, index) => (
                            <div key={index} className="indicator-box-row">
                              <span
                                style={{
                                  textAlign: "left",
                                }}
                              >
                                {getMLValue(param.label, i18n.language)}
                              </span>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: " #131722",
                                  textAlign: "right",
                                }}
                              >
                                {getMLValue(param.value, i18n.language)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                );
              } else if (item.type === "references_list") {
                return item.value.map((param: any, index: any) => (
                  <p
                    style={{ textAlign: "left", fontSize: "12px" }}
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: getMLValue(param.value),
                    }}
                  />
                ));
              } else if (item.type === "text") {
                return (
                  <div key={index} className="review-title">
                    {item.label && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: getMLValue(item.label, i18n.language),
                        }}
                      />
                    )}
                    <span
                      className="description-span"
                      dangerouslySetInnerHTML={{
                        __html: getMLValue(item.value, i18n.language),
                      }}
                    />
                  </div>
                );
              } else if (item.type === "params") {
                return (
                  <div key={index}>
                    <div className="review-title">
                      {item.label && (
                        <p>{getMLValue(item.label, i18n.language)}</p>
                      )}
                    </div>

                    {item.value.map((param: any, paramIndex: number) => (
                      <ReviewDetails
                        key={paramIndex}
                        title={getMLValue(param.label, i18n.language)}
                        info={getMLValue(param.value, i18n.language)}
                        type={param.type == "array" ? param.value : null}
                      />
                    ))}
                  </div>
                );
              } else if (item.type === "image") {
                return (
                  <div key={index} className="review-title">
                    {item.description && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getMLValue(item.description, i18n.language),
                        }}
                      />
                    )}
                    {item.label && (
                      <p>{getMLValue(item.label, i18n.language)}</p>
                    )}
                    {item.text && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getMLValue(item.text, i18n.language),
                        }}
                      />
                    )}
                    <img
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        marginTop: "12px",
                      }}
                      src={getCdnUrl(
                        `${symbol}/${getMLValue(item.value, i18n.language)}`,
                        "strategy",
                      )}
                      alt="Image"
                    />
                    {item.caption && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getMLValue(item.caption, i18n.language),
                        }}
                      />
                    )}
                  </div>
                );
              } else if (item.type === "list") {
                return (
                  <div key={index} className="review-title">
                    {item.label && (
                      <p className="review-list-label">
                        {getMLValue(item.label, i18n.language)}
                      </p>
                    )}
                    {item.text && (
                      <span
                        className="review-list-info"
                        dangerouslySetInnerHTML={{
                          __html: getMLValue(item.text, i18n.language),
                        }}
                      />
                    )}
                    <ul>
                      {item.value.map((listItem: any, listIndex: number) => (
                        <li
                          key={listIndex}
                          dangerouslySetInnerHTML={{
                            __html: getMLValue(listItem, i18n.language),
                          }}
                        />
                      ))}
                    </ul>
                  </div>
                );
              } else if (item.type === "companies") {
                return (
                  <div key={index} className="review-title">
                    <p>{getMLValue(item.label, i18n.language)}</p>
                    {item.value.map((company: any, companyIndex: number) => (
                      <div key={companyIndex} className="review-title-content">
                        <p>{getMLValue(company.name, i18n.language)}</p>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: getMLValue(
                              company.description,
                              i18n.language,
                            ),
                          }}
                        />
                        {company?.images?.map((image: any, index: number) => (
                          <img
                            key={index}
                            src={getCdnUrl(
                              `${symbol}/${getMLValue(image, i18n.language)}`,
                              "strategy",
                            )}
                            alt={getMLValue(image, i18n.language)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                );
              } else if (item.type === "disclaimer") {
                return (
                  <div className="indicators-title review-title">
                    <span className="description-span">
                      {getMLValue(item.text, i18n.language)}
                    </span>
                  </div>
                );
              } else if (item.type === "references") {
                return item.value.map((param) => (
                  <div key={index} className="indicators-info">
                    <p style={{ fontSize: "14px" }}>
                      {getMLValue(param.title, i18n.language)}
                    </p>
                    <span>
                      {t("sources")} {/* @ts-ignore */}
                      {param.list.map((item, index) => (
                        <span
                          style={{ fontWeight: 600, fontSize: "13px" }}
                          key={index}
                        >
                          {getMLValue(item.label, i18n.language)}
                          {index < param.list.length - 1 && ", "}
                        </span>
                      ))}
                    </span>
                    <ul>
                      {param.list.map((item, idx) => (
                        <li key={idx}>
                          <a
                            href={item.link[i18n.language]}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {getMLValue(item.link, i18n.language)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ));
              } else if (item.type === "chart") {
                return <ChartViewCustom assetId={item.value} />;
              }
            })}
          </div>
        ) : (
          <></>
        )}
      </div>

      {accountExists ? (
        <div className="strategy-action-btns-wrapper">
          {customInfo?.info?.hide_increase_decrease ? (
            <></>
          ) : (
            <>
              <ActionBtn
                action="Increase"
                onClick={() =>
                  action(() =>
                    navigateToAsset(
                      AssetType.StructuredProduct,
                      asset_id,
                      navigate,
                      OperationType.Buy,
                    ),
                  )
                }
                type="dark"
                disabled={!enableTrading}
              />
              <ActionBtn
                action="Decrease"
                onClick={() =>
                  action(() =>
                    navigateToAsset(
                      AssetType.StructuredProduct,
                      asset_id,
                      navigate,
                      OperationType.Sell,
                    ),
                  )
                }
                disabled={!enableTrading}
              />
            </>
          )}
        </div>
      ) : (
        <div className="btn-wrapper deal-btn-wrapper">
          <Button
            type="submit"
            variant="dark"
            className="customBtn deal-btn"
            disabled={isInvestButtonDisabled}
            onClick={() =>
              action(() =>
                navigateToAsset(
                  AssetType.StructuredProduct,
                  asset_id,
                  navigate,
                  OperationType.Buy,
                ),
              )
            }
          >
            {investButtonText}
          </Button>
        </div>
      )}

      {modal && (
        <Modal
          customModal="modalLogOut"
          onClick={onSignContract}
          onHide={() => {
            setModal(false);
            setDisabled(false);
          }}
          title={t("signContract")}
          text={t("enterPin")}
          btnText={t("sign")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal={true}
          isLoading={isLoading || disable}
          dealError={false}
        />
      )}
    </div>
  );
};
